import { useState, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Event, EVENT_ZONES } from '../../types/Event';
import { useAuth } from '../../contexts/AuthContext';
import { MEMBER_TYPES } from '../../types/User';

export const useEventsLogic = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const fetchEvents = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'events'));
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];

      const filteredEvents = eventsData.filter(event => {
        if (event.zone === EVENT_ZONES.HOME) {
          return user && (user.role === MEMBER_TYPES.GOLD || user.role === MEMBER_TYPES.ADMIN);
        }
        if (event.zone === EVENT_ZONES.WAN) {
          return !user || user.role !== MEMBER_TYPES.BRONZE;
        }
        if (event.zone === EVENT_ZONES.COMMUNAL) {
          return true;
        }
        return false;
      });

      setEvents(filteredEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleRegisterEvent = useCallback(async (
    selectedEvent: Event | null,
    setOpenDialog: (open: boolean) => void,
    idsToAdd?: string[],
    idsToRemove?: string[]
  ) => {
    if (!selectedEvent?.id || !user) return;

    try {
      const eventRef = doc(db, 'events', selectedEvent.id);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as Event;
      const currentParticipants = eventData.participants || [];

      // Mode gestion de famille (si idsToAdd ou idsToRemove est fourni)
      if (idsToAdd || idsToRemove) {
        const toAdd = idsToAdd || [];
        const toRemove = idsToRemove || [];

        // Vérification des places disponibles
        if (toAdd.length > 0 && eventData.maxParticipants) {
          // On ne compte que les NOUVEAUX ajouts (ceux qui ne sont pas déjà inscrits)
          const newRegistrationsCount = toAdd.filter(id => !currentParticipants.includes(id)).length;
          const availableSpots = eventData.maxParticipants - currentParticipants.length;

          if (currentParticipants.length + newRegistrationsCount > eventData.maxParticipants) {
            // On vérifie si les suppressions compensent
            const spotsFreed = toRemove.filter(id => currentParticipants.includes(id)).length;
            if (currentParticipants.length - spotsFreed + newRegistrationsCount > eventData.maxParticipants) {
              setSnackbar({
                open: true,
                message: `Pas assez de places disponibles. Il reste ${availableSpots} place(s).`,
                severity: 'error'
              });
              return;
            }
          }
        }

        // Application des modifications
        if (toRemove.length > 0) {
          await updateDoc(eventRef, {
            participants: arrayRemove(...toRemove)
          });
        }

        if (toAdd.length > 0) {
          await updateDoc(eventRef, {
            participants: arrayUnion(...toAdd)
          });
        }

        setSnackbar({
          open: true,
          message: 'Inscriptions mises à jour avec succès',
          severity: 'success'
        });

      } else {
        // Comportement Legacy (Toggle User)
        if (currentParticipants.includes(user.uid)) {
          await updateDoc(eventRef, {
            participants: arrayRemove(user.uid)
          });
        } else {
          if (currentParticipants.length >= eventData.maxParticipants) {
            setSnackbar({
              open: true,
              message: 'Désolé, l\'événement est complet',
              severity: 'error'
            });
            return;
          }
          await updateDoc(eventRef, {
            participants: arrayUnion(user.uid)
          });
        }

        setSnackbar({
          open: true,
          message: currentParticipants.includes(user.uid)
            ? 'Désinscription réussie'
            : 'Inscription réussie',
          severity: 'success'
        });
      }

      fetchEvents();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error registering for event:', error);
      setSnackbar({
        open: true,
        message: 'Une erreur est survenue',
        severity: 'error'
      });
    }
  }, [user, fetchEvents]);

  const handleDeleteEvent = useCallback(async (eventId: string | undefined) => {
    if (!eventId) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      try {
        await deleteDoc(doc(db, 'events', eventId));
        setSnackbar({
          open: true,
          message: 'Événement supprimé avec succès',
          severity: 'success'
        });
        fetchEvents();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setSnackbar({
          open: true,
          message: 'Erreur lors de la suppression de l\'événement',
          severity: 'error'
        });
      }
    }
  }, [fetchEvents]);

  return {
    events,
    fetchEvents,
    handleRegisterEvent,
    handleDeleteEvent,
    snackbar,
    setSnackbar
  };
};
